<script lang="ts">
  import { tick } from 'svelte';

  import Input from '$shared/atoms/Input/Input.svelte';
  import AutoCompleteList from './_partial/AutoCompleteList.svelte';
  import GlobalListHOC from '$shared/molecules/GlobalListHOC/GlobalListHOC.svelte';
  import {
    NavRowKind,
    NoHighlight,
    getSelectableIndices,
    moveHighlight,
    scrollRowIntoView,
    type NavRow
  } from '$shared/helpers/listNavigation';

  interface Props {
    loading?: boolean;
    canCreate?: boolean;
    showCreateInfo?: boolean;
    isEditable?: boolean;
    createLabel?: string;
    showRequiredInfo?: boolean;
    infoLabel?: string;
    label?: string;
    name?: string;
    value?: string;
    error?: string;
    placeholder?: string;
    disabled?: boolean;
    tabIndex?: number;
    required?: boolean;
    list?: any;
    hasOverflow?: boolean;
    canCreateOnInput?: boolean;
    onclick?: (item: any) => void;
    oncreate?: (value: string) => void;
    oncleared?: () => void;
    onclicklisticon?: (item: any) => void;
  }

  let {
    loading = false,
    canCreate = false,
    showCreateInfo = false,
    isEditable = false,
    createLabel = '',
    showRequiredInfo = false,
    infoLabel = '',
    label = '',
    name = '',
    value = $bindable(''),
    error = $bindable(''),
    placeholder = '',
    disabled = false,
    tabIndex = 1,
    required = true,
    list = [],
    hasOverflow = false,
    canCreateOnInput = false,
    onclick,
    oncreate,
    oncleared,
    onclicklisticon
  }: Props = $props();
  export const setValue = (val: string) => {
    value = val;
    selectedItem = val;
  };

  let parent: HTMLDivElement;
  let refInput: Input;
  let showList = $state(false);
  let filteredList: any[] = $state([]);
  let selectedItem: any = null;
  let appWithList: GlobalListHOC;
  let listContainer: HTMLElement | undefined = $state();
  let highlightedIndex = $state(NoHighlight);
  const listId = $props.id();

  // Mirrors the render conditions in AutoCompleteList: the info branch replaces
  // the list entirely, and CustomCreate renders nothing without a value.
  let showsInfoOnly = $derived(showRequiredInfo && !!infoLabel);
  let isValueMatch = $derived(
    showCreateInfo ? !!filteredList.find((item) => item.label === value) : false
  );
  let showCreateRow = $derived(
    (canCreate || canCreateOnInput) &&
      !!value &&
      (filteredList.length === 0 || (showCreateInfo && !isValueMatch))
  );

  let rows: NavRow[] = $derived(
    showsInfoOnly
      ? []
      : [
          ...filteredList.map((item) => ({
            kind: NavRowKind.Item,
            item,
            selectable: !item.disabled
          })),
          ...(showCreateRow
            ? [{ kind: NavRowKind.Create, item: { label: value }, selectable: true }]
            : [])
        ]
  );

  let selectableIndices = $derived(getSelectableIndices(rows));

  $effect(() => {
    if (!value || value === '') {
      filteredList = [...list];
    } else {
      filteredList = list.filter((item) =>
        item?.label?.toLowerCase().includes(value.toLowerCase())
      );
    }
  });

  const onClickListItem = (item): void => {
    highlightedIndex = NoHighlight;
    selectedItem = item;
    value = item.label || '';
    onclick?.(item);
    showList = false;
    removeInputFocus();
  };

  const onInputChange = (detail: { value: string }): void => {
    value = detail.value.trim() || '';
    highlightedIndex = NoHighlight;

    if (canCreateOnInput) {
      oncreate?.(value);
      selectedItem = value;
      return;
    }
    selectedItem = null;
    oncleared?.();
  };

  const onShowList = (): void => {
    showList = true;
    highlightedIndex = NoHighlight;
    tick().then(() => {
      appWithList?.setListPosition();
    });
  };

  const onBlur = (): void => {
    setTimeout(() => {
      if (canCreate || canCreateOnInput) {
        if (!selectedItem) {
          value = '';
          showList = false;
        }
        return;
      }
      const filteredValue = filteredList.find(
        (item) => item?.label?.toLowerCase() === value?.toLowerCase()
      );
      if (!filteredValue) {
        value = '';
        selectedItem = null;
        oncleared?.();
      }
    }, 200);
  };

  const setHighlight = (index: number): void => {
    if (index === NoHighlight) return;
    highlightedIndex = index;
    scrollRowIntoView(listContainer, index);
  };

  /**
   * Scoped to the wrapper so one listener covers the input and the list, which
   * both sit inside it. Enter routes through onClickListItem/onClickCreate
   * verbatim: both set `selectedItem` before blurring, so the 200ms onBlur
   * timeout does not clear the freshly-made selection.
   */
  const onKeyDown = (e: KeyboardEvent): void => {
    if (disabled) return;

    if (!showList) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        onShowList();
      }
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      showList = false;
      highlightedIndex = NoHighlight;
      return;
    }

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(
        moveHighlight(highlightedIndex, selectableIndices, e.key === 'ArrowDown' ? 1 : -1)
      );
      return;
    }

    if (e.key === 'Enter') {
      const row = rows[highlightedIndex];
      if (!row?.selectable) return;
      e.preventDefault();
      if (row.kind === NavRowKind.Create) {
        onClickCreate();
      } else {
        onClickListItem(row.item);
      }
    }
  };

  const onClickWindow = (e: any): void => {
    if (parent && !parent.contains(e.target)) {
      showList = false;
    }
  };

  const onClickCreate = (): void => {
    oncreate?.(value);
    selectedItem = value;
    showList = false;
    removeInputFocus();
  };

  const focusInput = () => {
    refInput.focus();
  };

  const removeInputFocus = () => {
    refInput.blur();
  };

  const onIconClick = (item) => {
    showList = false;
    oncleared?.();
    onclicklisticon?.(item);
    removeInputFocus();
  };
</script>

<svelte:window onmouseup={onClickWindow} />

{#snippet labelSnippet()}
  <div class="dropdown-input" title={value}>
    <Input
      bind:this={refInput}
      icon="icon-chevron-down"
      {loading}
      {label}
      {name}
      {value}
      {error}
      {placeholder}
      {disabled}
      {tabIndex}
      {required}
      role="combobox"
      ariaExpanded={showList}
      ariaControls={listId}
      ariaActiveDescendant={highlightedIndex === NoHighlight
        ? undefined
        : `${listId}-opt-${highlightedIndex}`}
      onfocus={onShowList}
      onblur={onBlur}
      oninput={onInputChange}
      onclickicon={onShowList}
    />
  </div>
{/snippet}

{#snippet listSnippet()}
  <div
    bind:this={listContainer}
    data-cy={`${name}-list`}
    class="list"
    onmousedown={(e) => {
      e.preventDefault();
      focusInput();
    }}
  >
    <AutoCompleteList
      {filteredList}
      {highlightedIndex}
      {listId}
      canCreate={canCreate || canCreateOnInput}
      {showCreateInfo}
      {createLabel}
      {value}
      {isEditable}
      {showRequiredInfo}
      {infoLabel}
      onclickListItem={onClickListItem}
      oniconClick={onIconClick}
      onclickCreate={onClickCreate}
    />
  </div>
{/snippet}

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div bind:this={parent} class="comp-auto-complete" onkeydown={onKeyDown}>
  <GlobalListHOC
    bind:this={appWithList}
    show={!disabled && showList}
    {hasOverflow}
    label={labelSnippet}
    list={listSnippet}
  />
</div>

<style>
  .comp-auto-complete {
    position: relative;
  }
  .list {
    display: flex;
    background-color: #fff;
    flex-direction: column;
    border: 1px solid #e5e5e5;
    box-shadow:
      0px 3px 11px 1px rgba(0, 0, 0, 0.0783708),
      0px 7px 18px 3px rgba(0, 0, 0, 0.1);
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    max-height: 200px;
    z-index: 99;
    overflow: auto;
  }
</style>
